// features/Playbook.js
// Playbook feature — ported to new component system.
// Shell replaced with ShiftPanel. Nav state moved inside.
// Content logic unchanged from index.js lines 14439-14975.
//
// Props from parent (index.js):
//   onClose, isMobile, userRecord
//   playbookItems, playbookLoading
//   deletePlaybookItem, startGameplan, seedPlaybook
//   createPlaybookItem
//   showPlaybookCreate, setShowPlaybookCreate
//   pbCreateType, setPbCreateType
//   pbCreateTitle, setPbCreateTitle
//   pbCreateContent, setPbCreateContent
//   pbCreateTags, setPbCreateTags

import { useState } from 'react';
import ShiftCard from '../components/shift-card';
import ShiftPanelHeader from '../components/shift-panel-header';

// ── TAXONOMY — module-level constants ─────────────────────
const PLAYBOOK_TAXONOMY = {
  lenses: {
    label: 'Lenses', color: 'var(--purple)',
    types: ['mental_model', 'framework', 'heuristic', 'principle', 'decision_matrix', 'snippet', 'prompt'],
  },
  plays: {
    label: 'Plays', color: 'var(--gold)',
    types: ['template', 'process', 'sop', 'workflow', 'sequence', 'checklist', 'routine', 'algorithm', 'pipeline', 'protocol', 'method', 'function', 'lifecycle'],
  },
  reads: {
    label: 'Reads', color: 'var(--green)',
    types: ['cheatsheet', 'diagnostic', 'rubric', 'benchmark', 'anti_pattern', 'trigger', 'exit_criteria', 'scenario', 'calibration'],
  },
  frames: {
    label: 'Frames', color: 'var(--teal)',
    types: ['structure', 'taxonomy', 'layout', 'constraint_set', 'flowchart', 'formula', 'blueprint'],
  },
};

const PLAYBOOK_L2_MAP = {
  lenses: [['mental_model', 'Mental Model'], ['framework', 'Framework'], ['heuristic', 'Heuristic']],
  plays:  [['template', 'Template'], ['sop', 'SOP'], ['workflow', 'Workflow']],
  reads:  [['cheatsheet', 'Cheatsheet'], ['diagnostic', 'Diagnostic'], ['rubric', 'Rubric']],
  frames: [['structure', 'Structure'], ['taxonomy', 'Taxonomy'], ['blueprint', 'Blueprint']],
};

const TYPE_LABELS = {
  mental_model: 'Mental Model', framework: 'Framework', heuristic: 'Heuristic',
  principle: 'Principle', decision_matrix: 'Decision Matrix', snippet: 'Snippet', prompt: 'Prompt',
  template: 'Template', process: 'Process', sop: 'SOP', workflow: 'Workflow',
  sequence: 'Sequence', checklist: 'Checklist', routine: 'Routine', algorithm: 'Algorithm',
  pipeline: 'Pipeline', protocol: 'Protocol', method: 'Method', function: 'Function', lifecycle: 'Lifecycle',
  cheatsheet: 'Cheatsheet', diagnostic: 'Diagnostic', rubric: 'Rubric', benchmark: 'Benchmark',
  anti_pattern: 'Anti-Pattern', trigger: 'Trigger', exit_criteria: 'Exit Criteria',
  scenario: 'Scenario', calibration: 'Calibration',
  structure: 'Structure', taxonomy: 'Taxonomy', layout: 'Layout', constraint_set: 'Constraints',
  flowchart: 'Flowchart', formula: 'Formula', blueprint: 'Blueprint',
};

// ── HELPERS ───────────────────────────────────────────────
function getItemCategory(itemType) {
  for (const [cat, def] of Object.entries(PLAYBOOK_TAXONOMY)) {
    if (def.types.includes(itemType)) return cat;
  }
  return 'plays';
}

function getItemPreview(item, maxLen = 100) {
  const c = item.content;
  if (!c) return null;
  let text = null;
  if (typeof c === 'string') {
    text = c;
  } else if (typeof c === 'object') {
    text = c.purpose || c.use_case || c.prompt_text || c.description ||
           c.summary || c.overview || c.instruction ||
           (Array.isArray(c.sections) ? c.sections.map(s => s.title || s.label || s.key || (Array.isArray(s.rows) ? s.rows.map(r => r.key || r.label || '').filter(Boolean).join(', ') : '') || '').filter(Boolean).join(' · ') : null) ||
           (Array.isArray(c.steps)    ? c.steps.map(s => s.value || s.label || s.title || '').filter(Boolean).join(' → ') : null) ||
           (Array.isArray(c.blocks)   ? c.blocks.map(b => b.value || b.label || b.title || '').filter(Boolean).join(' ') : null) ||
           c.structure || c.content || c.text || null;
  }
  if (!text) return null;
  text = String(text)
    .replace(/```[\s\S]*?```/g, '').replace(/`(.*?)`/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/#{1,6}\s?/g, '').replace(/(^|\s)>\s/g, ' ')
    .replace(/(^|\s)[-*+]\s/g, ' ').replace(/(^|\s)\d+\.\s/g, ' ')
    .replace(/\n/g, ' ').replace(/\s{2,}/g, ' ').trim();
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > maxLen * 0.6 ? cut.slice(0, lastSpace) : cut) + '…';
}

// ── TRASH ICON SVG ────────────────────────────────────────
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3h4v1M5 4l1 9h4l1-9"
      stroke="currentColor" strokeWidth="1.3"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── QUICK-CREATE OVERLAY ──────────────────────────────────
function PlaybookCreateOverlay({
  pbCreateType, setPbCreateType,
  pbCreateTitle, setPbCreateTitle,
  pbCreateContent, setPbCreateContent,
  pbCreateTags, setPbCreateTags,
  createPlaybookItem,
  onClose,
}) {
  const canSave = pbCreateTitle.trim() && pbCreateContent.trim();

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, left: 'var(--sidebar-width)',
      background: 'var(--surface-0)',
      zIndex: 110,                     /* above panel-overlay z:100 */
      display: 'flex', flexDirection: 'column',
      fontFamily: 'var(--font-serif)',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 28px',
        borderBottom: '1px solid var(--border-4)',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexShrink: 0,
      }}>
        <span style={{ color: 'var(--gold)', fontSize: '15px', fontWeight: 'bold' }}>
          Add to Playbook
        </span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--text-5)',
          cursor: 'pointer', fontSize: '20px', lineHeight: 1,
        }}>×</button>
      </div>

      {/* Form */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

        {/* Type selector - grouped by category */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ color: 'var(--text-5)', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>
            Type
          </div>
          {Object.entries(PLAYBOOK_TAXONOMY).map(([catKey, catDef]) => (
            <div key={catKey} style={{ marginBottom: '14px' }}>
              <div style={{ color: catDef.color, fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
                {catDef.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {catDef.types.map(t => {
                  const isActive = pbCreateType === t;
                  return (
                    <button key={t} onClick={() => setPbCreateType(t)} style={{
                      background: isActive ? `${catDef.color}1a` : 'var(--surface-4)',
                      border: `1px solid ${isActive ? catDef.color : 'var(--border-1)'}`,
                      borderRadius: 'var(--radius-sm)',
                      color: isActive ? catDef.color : 'var(--text-5)',
                      fontSize: '11px', fontFamily: 'var(--font-serif)',
                      padding: '4px 12px', cursor: 'pointer',
                      transition: 'all var(--transition-fast)',
                    }}>
                      {TYPE_LABELS[t] || t}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Title */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ color: 'var(--text-5)', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>Title</div>
          <input
            value={pbCreateTitle}
            onChange={e => setPbCreateTitle(e.target.value)}
            placeholder={`Name this ${TYPE_LABELS[pbCreateType] || pbCreateType}…`}
            style={{
              width: '100%', background: 'var(--surface-3)',
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--radius-md)', color: 'var(--text-1)',
              fontSize: '14px', fontFamily: 'var(--font-serif)',
              padding: '10px 14px', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color var(--transition-base)',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--gold)'}
            onBlur={e => e.target.style.borderColor = 'var(--border-2)'}
          />
        </div>

        {/* Content */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ color: 'var(--text-5)', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>Content</div>
          <textarea
            value={pbCreateContent}
            onChange={e => setPbCreateContent(e.target.value)}
            placeholder="Paste or write the content…"
            rows={6}
            style={{
              width: '100%', background: 'var(--surface-3)',
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--radius-md)', color: 'var(--text-1)',
              fontSize: '13px', fontFamily: 'var(--font-serif)',
              lineHeight: '1.7', padding: '10px 14px', outline: 'none',
              resize: 'vertical', boxSizing: 'border-box',
              transition: 'border-color var(--transition-base)',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--gold)'}
            onBlur={e => e.target.style.borderColor = 'var(--border-2)'}
          />
        </div>

        {/* Tags */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ color: 'var(--text-5)', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
            Tags{' '}
            <span style={{ color: 'var(--text-8)', textTransform: 'none', letterSpacing: 0, fontSize: '11px' }}>
              — optional, comma separated
            </span>
          </div>
          <input
            value={pbCreateTags}
            onChange={e => setPbCreateTags(e.target.value)}
            placeholder="e.g. sales, objections, pricing"
            style={{
              width: '100%', background: 'var(--surface-3)',
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--radius-md)', color: 'var(--text-1)',
              fontSize: '13px', fontFamily: 'var(--font-serif)',
              padding: '8px 14px', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color var(--transition-base)',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--gold)'}
            onBlur={e => e.target.style.borderColor = 'var(--border-2)'}
          />
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '14px 28px 24px',
        borderTop: '1px solid var(--border-5)',
        flexShrink: 0, display: 'flex',
        justifyContent: 'flex-end', gap: '10px',
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: '1px solid var(--border-1)',
            borderRadius: 'var(--radius-md)', color: 'var(--text-5)',
            fontSize: '13px', fontFamily: 'var(--font-serif)',
            padding: '9px 22px', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={createPlaybookItem}
          disabled={!canSave}
          style={{
            background: canSave ? 'var(--gold-bg)' : 'none',
            border: `1px solid ${canSave ? 'var(--gold)' : 'var(--border-1)'}`,
            borderRadius: 'var(--radius-md)',
            color: canSave ? 'var(--gold)' : 'var(--text-7)',
            fontSize: '13px', fontFamily: 'var(--font-serif)',
            letterSpacing: '0.04em', padding: '9px 28px',
            cursor: canSave ? 'pointer' : 'default',
            transition: 'all var(--transition-base)',
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}

// ── SEARCH + VIEW TOOLBAR ─────────────────────────────────
function PlaybookToolbar({ search, setSearch, placeholder, selectMode, setSelectMode, selectedCount }) {
  return (
    <div className="cards-toolbar" style={{ borderBottom: '1px solid var(--border-1)', background: 'var(--surface-1)', flexShrink: 0 }}>
      <div className="cards-search-wrap">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="6.5" cy="6.5" r="5" stroke="var(--text-5)" strokeWidth="1.4"/>
          <path d="M10.5 10.5L14 14" stroke="var(--text-5)" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          className="cards-search"
          placeholder={placeholder || 'Search…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onFocus={e => e.target.style.borderColor = 'var(--gold)'}
          onBlur={e => e.target.style.borderColor = 'var(--border-2)'}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={() => setSelectMode(s => !s)}
          className={`select-btn${selectMode ? ' active' : ''}`}
        >
          {selectMode ? `Selected (${selectedCount})` : 'Select'}
        </button>
      </div>
    </div>
  );
}

// ── ITEM CARD (grid) ──────────────────────────────────────
function ItemCardGrid({ item, accentColor, typeLabel, preview, onDelete, onGameplan, selectMode, isSelected, onSelect }) {
  const createdDate = item.created_at
    ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const lastUsed = item.last_used_at
    ? new Date(item.last_used_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const bullets = Array.isArray(item.summary_bullets) ? item.summary_bullets : [];

  return (
    <div
      onClick={() => selectMode && !item._fromCommunity && onSelect(item.id)}
      style={{
        padding: '20px 24px 16px', borderRadius: 'var(--radius-lg)',
        background: item._fromCommunity ? 'var(--green-surface)' : isSelected ? 'var(--gold-surface)' : 'var(--surface-2)',
        border: `1px solid ${isSelected ? 'var(--gold-border)' : item._fromCommunity ? 'var(--green-border)' : 'var(--border-3)'}`,
        borderLeft: `3px solid ${accentColor}`,
        display: 'flex', flexDirection: 'column', gap: '10px',
        cursor: selectMode && !item._fromCommunity ? 'pointer' : 'default',
        position: 'relative', transition: 'background var(--transition-fast)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = item._fromCommunity ? 'var(--green-bg)' : 'var(--surface-6)';
        const d = e.currentTarget.querySelector('.pb-del');
        if (d) d.style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = item._fromCommunity ? 'var(--green-surface)' : isSelected ? 'var(--gold-surface)' : 'var(--surface-2)';
        const d = e.currentTarget.querySelector('.pb-del');
        if (d) d.style.opacity = '0';
      }}
    >
      {/* Select checkbox */}
      {selectMode && !item._fromCommunity && (
        <div style={{
          width: '18px', height: '18px', borderRadius: 'var(--radius-sm)',
          border: `2px solid ${isSelected ? 'var(--gold)' : 'var(--text-7)'}`,
          background: isSelected ? 'var(--gold)' : 'transparent',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isSelected && <span style={{ color: 'var(--surface-0)', fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>✓</span>}
        </div>
      )}

      {/* Top row — type badge left, created date right */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '24px' }}>
        <span style={{
          background: `${accentColor}22`, border: `1px solid ${accentColor}99`,
          borderRadius: 'var(--radius-pill)', color: accentColor,
          fontSize: '11px', letterSpacing: '0.07em', padding: '2px 10px',
          fontFamily: 'var(--font-serif)', flexShrink: 0, fontWeight: 'bold',
        }}>
          {typeLabel}
        </span>
        {createdDate && (
          <span style={{ color: 'var(--text-8)', fontSize: '11px', fontFamily: 'var(--font-serif)', letterSpacing: '0.03em' }}>
            {createdDate}
          </span>
        )}
      </div>

      {/* Title */}
      <div style={{ color: 'var(--text-1)', fontSize: '15px', fontFamily: 'var(--font-serif)', fontWeight: 'bold', lineHeight: '1.35' }}>
        {item.title}
      </div>

      {/* Body — bullets → preview → empty state */}
      {bullets.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ color: accentColor, fontSize: '11px', flexShrink: 0, marginTop: '3px', lineHeight: 1 }}>—</span>
              <span style={{ color: 'var(--text-4)', fontSize: '13px', lineHeight: '1.65', fontFamily: 'var(--font-serif)' }}>{b}</span>
            </div>
          ))}
        </div>
      ) : preview ? (
        <div style={{ color: 'var(--text-4)', fontSize: '13px', lineHeight: '1.7', fontFamily: 'var(--font-serif)' }}>
          {preview}
        </div>
      ) : (
        <div style={{ color: 'var(--text-8)', fontSize: '12px', fontStyle: 'italic', fontFamily: 'var(--font-serif)' }}>
          Play this item to build a summary.
        </div>
      )}

      {item._fromCommunity && (
        <div style={{ color: 'var(--green)', fontSize: '11px' }}>community</div>
      )}

      {/* Bottom row — last used left, Gameplan button right */}
      {!item._fromCommunity && !selectMode && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
          <span style={{ color: 'var(--text-8)', fontSize: '11px', fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
            {lastUsed ? `last used ${lastUsed}` : ''}
          </span>
          <button
            onClick={e => { e.stopPropagation(); onGameplan(item); }}
            style={{
              background: 'var(--green-bg)', border: '1px solid var(--green)',
              borderRadius: 'var(--radius-sm)', color: 'var(--green)',
              fontSize: '12px', fontFamily: 'var(--font-serif)',
              padding: '6px 16px', cursor: 'pointer', letterSpacing: '0.04em',
              transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--green-border)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--green-bg)'}
          >
            Gameplan →
          </button>
        </div>
      )}

      {/* Delete — top right on hover */}
      {!item._fromCommunity && !selectMode && (
        <button className="pb-del" onClick={e => { e.stopPropagation(); onDelete(item.id); }} style={{
          position: 'absolute', top: '14px', right: '14px',
          background: 'none', border: 'none', color: 'var(--danger)',
          cursor: 'pointer', padding: '2px', opacity: 0,
          transition: 'opacity var(--transition-fast)',
        }}>
          <TrashIcon />
        </button>
      )}
    </div>
  );
}

// ── ITEM ROW (list) ───────────────────────────────────────
function ItemRowList({ item, accentColor, typeLabel, preview, onDelete, onGameplan, selectMode, isSelected, onSelect }) {
  return (
    <div
      onClick={() => selectMode && !item._fromCommunity && onSelect(item.id)}
      style={{
        padding: '16px 20px', marginBottom: '8px',
        background: item._fromCommunity ? 'var(--green-surface)' : isSelected ? 'var(--gold-surface)' : 'var(--surface-2)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${isSelected ? 'var(--gold-border)' : item._fromCommunity ? 'var(--green-border)' : 'var(--border-3)'}`,
        borderLeft: `3px solid ${accentColor}`,
        cursor: selectMode && !item._fromCommunity ? 'pointer' : 'default',
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        transition: 'background var(--transition-fast)', position: 'relative',
      }}
      onMouseEnter={e => {
        if (!selectMode) e.currentTarget.style.background = item._fromCommunity ? 'var(--green-bg)' : 'var(--surface-6)';
        const d = e.currentTarget.querySelector('.pb-del-l');
        if (d) d.style.opacity = '1';
      }}
      onMouseLeave={e => {
        if (!selectMode) e.currentTarget.style.background = item._fromCommunity ? 'var(--green-surface)' : isSelected ? 'var(--gold-surface)' : 'var(--surface-2)';
        const d = e.currentTarget.querySelector('.pb-del-l');
        if (d) d.style.opacity = '0';
      }}
    >
      {selectMode && !item._fromCommunity && (
        <div style={{
          width: '18px', height: '18px', borderRadius: 'var(--radius-sm)',
          border: `2px solid ${isSelected ? 'var(--gold)' : 'var(--text-7)'}`,
          background: isSelected ? 'var(--gold)' : 'transparent',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px',
        }}>
          {isSelected && <span style={{ color: 'var(--surface-0)', fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>✓</span>}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <span style={{
            background: `${accentColor}22`, border: `1px solid ${accentColor}99`,
            borderRadius: 'var(--radius-sm)', color: accentColor,
            fontSize: '12px', letterSpacing: '0.07em', padding: '2px 8px',
            fontFamily: 'var(--font-serif)', flexShrink: 0,
          }}>
            {typeLabel}
          </span>
          <div style={{ color: 'var(--text-1)', fontSize: '15px', fontFamily: 'var(--font-serif)' }}>{item.title}</div>
          {item._fromCommunity && <span style={{ color: 'var(--green)', fontSize: '11px' }}>community</span>}
          {item.use_count > 0 && <span style={{ color: 'var(--text-5)', fontSize: '11px' }}>· {item.use_count}×</span>}
        </div>
        {preview && <div style={{ color: 'var(--text-4)', fontSize: '13px', lineHeight: '1.6', fontFamily: 'var(--font-serif)' }}>{preview}</div>}
      </div>
      {!item._fromCommunity && !selectMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={e => { e.stopPropagation(); onGameplan(item); }}
            style={{
              background: 'var(--green-bg)', border: '1px solid var(--green)',
              borderRadius: 'var(--radius-sm)', color: 'var(--green)',
              fontSize: '11px', fontFamily: 'var(--font-serif)',
              padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.04em', whiteSpace: 'nowrap',
              transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--green-border)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--green-bg)'}
          >
            Gameplan →
          </button>
          <button className="pb-del-l" onClick={e => { e.stopPropagation(); onDelete(item.id); }} style={{
            background: 'none', border: 'none', color: 'var(--danger)',
            cursor: 'pointer', padding: '4px', opacity: 0,
            transition: 'opacity var(--transition-fast)',
            display: 'flex', alignItems: 'center',
          }}>
            <TrashIcon />
          </button>
        </div>
      )}
    </div>
  );
}

// ── MAIN PLAYBOOK COMPONENT ───────────────────────────────
export default function Playbook({
  onClose, isMobile, userRecord,
  playbookItems, playbookLoading,
  deletePlaybookItem, startGameplan, seedPlaybook,
  createPlaybookItem,
  showPlaybookCreate, setShowPlaybookCreate,
  pbCreateType, setPbCreateType,
  pbCreateTitle, setPbCreateTitle,
  pbCreateContent, setPbCreateContent,
  pbCreateTags, setPbCreateTags,
  onCreateWithShift,
}) {
  // ── Internal nav state (moved from index.js) ─────────
  const [activeL1, setActiveL1] = useState('all');
  const [activeL2, setActiveL2] = useState('template');
  const [activeL3, setActiveL3] = useState('mine');
  const [search, setSearch]     = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleL1Change(type) {
    setActiveL1(type);
    setSearch('');
    setSelectMode(false);
    setSelected(new Set());
    if (PLAYBOOK_L2_MAP[type]) setActiveL2(PLAYBOOK_L2_MAP[type][0][0]);
    setActiveL3('mine');
  }

  const aiName = userRecord?.ai_name || 'Shift';
  const l1Color = PLAYBOOK_TAXONOMY[activeL1]?.color || 'var(--gold)';

  const TAB_KEY = { 'All': 'all', 'Lenses': 'lenses', 'Plays': 'plays', 'Reads': 'reads', 'Frames': 'frames' };
  const activeTab = { all: 'All', lenses: 'Lenses', plays: 'Plays', reads: 'Reads', frames: 'Frames' }[activeL1] || 'All';
  const setActiveTab = (label) => handleL1Change(TAB_KEY[label] || 'all');
  // Header button — prefers the chat-based flow if provided
  const handleCreate = () => {
    if (onCreateWithShift) { onCreateWithShift(); return; }
    setPbCreateType('process'); setShowPlaybookCreate(true);
  };

  // ── TOOLBAR (all nav bars — non-scrolling) ────────────
  const toolbar = (
    <>
      {/* L2 — type pills (when not All) */}
      {activeL1 !== 'all' && PLAYBOOK_L2_MAP[activeL1] && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: '8px', padding: '10px 28px',
          borderBottom: '1px solid var(--border-5)',
          flexShrink: 0, background: 'var(--surface-0)',
        }}>
          {PLAYBOOK_L2_MAP[activeL1].map(([type, label]) => {
            const isActive = activeL2 === type;
            return (
              <button key={type} onClick={() => { setActiveL2(type); setActiveL3('mine'); }} style={{
                background: isActive ? `${l1Color}22` : 'transparent',
                border: `1px solid ${isActive ? l1Color : 'var(--border-1)'}`,
                borderRadius: 'var(--radius-pill)',
                color: isActive ? l1Color : 'var(--text-5)',
                fontSize: '12px', fontFamily: 'var(--font-serif)',
                letterSpacing: '0.06em', padding: '5px 16px', cursor: 'pointer',
                outline: 'none', transition: 'all var(--transition-fast)', whiteSpace: 'nowrap',
              }}
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = 'var(--border-2)'; e.currentTarget.style.color = 'var(--text-3)'; } }}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = 'var(--border-1)'; e.currentTarget.style.color = 'var(--text-5)'; } }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* L3 — segmented control (when not All) */}
      {activeL1 !== 'all' && (
        <div style={{
          display: 'flex', justifyContent: 'center',
          padding: '10px 28px',
          borderBottom: '1px solid var(--border-5)',
          flexShrink: 0, background: 'var(--surface-0)',
        }}>
          <div style={{
            display: 'inline-flex', background: 'var(--surface-2)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--radius-md)', overflow: 'hidden',
          }}>
            {[
              ['mine',      `My ${TYPE_LABELS[activeL2] || activeL2}s`],
              ['gameplan',  'Gameplan'],
              ['community', 'Community'],
            ].map(([sub, label], idx, arr) => (
              <button key={sub} onClick={() => setActiveL3(sub)} style={{
                background: activeL3 === sub ? 'var(--surface-5)' : 'transparent',
                border: 'none',
                borderRight: idx < arr.length - 1 ? '1px solid var(--border-1)' : 'none',
                outline: 'none', cursor: 'pointer',
                color: activeL3 === sub ? 'var(--text-1)' : 'var(--text-7)',
                fontSize: '11px', fontFamily: 'var(--font-serif)',
                letterSpacing: '0.06em', padding: '6px 18px',
                whiteSpace: 'nowrap', transition: 'all var(--transition-fast)',
              }}
                onMouseEnter={e => { if (activeL3 !== sub) e.currentTarget.style.color = 'var(--text-4)'; }}
                onMouseLeave={e => { if (activeL3 !== sub) e.currentTarget.style.color = 'var(--text-7)'; }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search + view toolbar */}
      <PlaybookToolbar
        search={search}
        setSearch={setSearch}
        placeholder={activeL1 === 'all' ? 'Search your playbook…' : `Search ${TYPE_LABELS[activeL2] || activeL2}s…`}
        selectMode={selectMode}
        setSelectMode={s => { setSelectMode(s); if (!s) setSelected(new Set()); }}
        selectedCount={selected.size}
      />
    </>
  );

  // ── CONTENT renderer ──────────────────────────────────
  function renderContent() {
    const isGrid = true;

    // ── ALL TAB ──────────────────────────────────────
    if (activeL1 === 'all') {
      if (playbookLoading) return <div style={{ color: 'var(--text-6)', fontSize: '13px', fontStyle: 'italic' }}>loading…</div>;

      const filtered = playbookItems.filter(item =>
        !search || item.title.toLowerCase().includes(search.toLowerCase())
      );
      const personal  = filtered.filter(i => !i._fromCommunity);
      const community = filtered.filter(i =>  i._fromCommunity);

      const renderAllItem = item => {
        const itemCat   = item.playbook_category || getItemCategory(item.item_type);
        const catDef    = PLAYBOOK_TAXONOMY[itemCat] || {};
        const typeLabel = TYPE_LABELS[item.item_type] || item.item_type;
        const color     = catDef.color || 'var(--text-7)';
        const preview   = getItemPreview(item, 100);
        const isSelected = selected.has(item.id);
        const props = { item, accentColor: color, typeLabel, preview, actionLabel: 'Gameplan →', onAction: startGameplan, onDelete: deletePlaybookItem, selectMode, isSelected, onSelect: toggleSelect, fromCommunity: item._fromCommunity };
        return isGrid
          ? <ShiftCard key={item.id} {...props} />
          : <ItemRowList  key={item.id} {...props} />;
      };

      if (filtered.length === 0 && playbookItems.length > 0) {
        return <div style={{ color: 'var(--text-7)', fontSize: '13px', fontStyle: 'italic' }}>No items match your search.</div>;
      }

      if (community.length > 0) {
        return (
          <div className={isGrid ? 'cards-grid' : undefined} style={{ display: isGrid ? 'grid' : 'block' }}>
            {personal.map(renderAllItem)}
            <div style={{ color: 'var(--text-5)', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '16px 0 10px', borderTop: '1px solid var(--border-1)', marginTop: '6px', gridColumn: isGrid ? '1 / -1' : undefined }}>
              From the community
            </div>
            {community.map(renderAllItem)}
          </div>
        );
      }
      return <div className={isGrid ? 'cards-grid' : undefined} style={{ display: isGrid ? 'grid' : 'block' }}>{filtered.map(renderAllItem)}</div>;
    }

    // ── L3: MINE ─────────────────────────────────────
    if (activeL3 === 'mine') {
      const myItems = playbookItems.filter(i => i.item_type === activeL2 && !i._fromCommunity);

      if (myItems.length === 0) {
        const emptyMeta = {
          mental_model:   { icon: '🧠', what: 'A mental model is a framework for how something works — a lens you apply to think more clearly about a class of problems.',       example: '"Inversion: instead of asking what makes this succeed, ask what would make it fail."' },
          framework:      { icon: '🗂', what: 'A framework is a repeatable structure for approaching a type of decision or problem.',                                              example: '"SWOT: Strengths, Weaknesses, Opportunities, Threats — applied before any major move."' },
          heuristic:      { icon: '⚡', what: 'A heuristic is a fast rule of thumb — a shortcut that gets you 80% of the way there without full analysis.',                      example: '"If a decision is reversible, move fast. If it\'s not, slow down."' },
          template:       { icon: '📋', what: 'A template is a reusable structure for a recurring output.',                                                                       example: '"Pre-Mortem: run this before any major commitment to surface risks early."' },
          sop:            { icon: '📌', what: 'A Standard Operating Procedure is a step-by-step process for a task you do repeatedly — so you never have to think from scratch.', example: '"Weekly close: review pipeline, update CRM, send team update, flag blockers."' },
          workflow:       { icon: '🔄', what: 'A workflow maps how work moves from start to finish.',                                                                              example: '"Client onboarding: signed → kickoff booked → brief sent → assets delivered."' },
          cheatsheet:     { icon: '📝', what: 'A cheatsheet is a quick-reference card for syntax, commands, or rules you use often but don\'t want to memorize.',                 example: '"SQL joins: INNER, LEFT, RIGHT, FULL — with a one-line use case for each."' },
          diagnostic:     { icon: '🔍', what: 'A diagnostic is a set of questions or signals that help you identify what\'s wrong before jumping to solutions.',                  example: '"Is this a strategy problem, an execution problem, or a people problem?"' },
          rubric:         { icon: '⚖️', what: 'A rubric is a scoring system for evaluating options consistently.',                                                                example: '"Rate each hire candidate on: communication, coachability, domain knowledge, drive."' },
          structure:      { icon: '🏗', what: 'A structure is a template for organizing information — a repeatable layout.',                                                      example: '"One-pager: problem → insight → solution → ask → risks."' },
          taxonomy:       { icon: '🌿', what: 'A taxonomy is a classification system — a way to group and label things consistently.',                                            example: '"Customer segments: Champion, Skeptic, Blocker, Bystander."' },
          blueprint:      { icon: '🔷', what: 'A blueprint is a high-level system design — the architecture of how components connect.',                                          example: '"Growth loop: acquisition → activation → retention → referral → revenue."' },
        };
        const meta = emptyMeta[activeL2] || { icon: '📁', what: `A ${TYPE_LABELS[activeL2] || activeL2} is a reusable pattern that helps you think or act more effectively.`, example: null };
        return (
          <div style={{ padding: '48px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', maxWidth: '480px', margin: '0 auto' }}>
            <div style={{ fontSize: '36px', lineHeight: 1 }}>{meta.icon}</div>
            <div style={{ color: 'var(--text-1)', fontSize: '16px', fontFamily: 'var(--font-serif)', fontWeight: 'bold', textAlign: 'center' }}>
              No {TYPE_LABELS[activeL2] || activeL2}s yet
            </div>
            <div style={{ color: 'var(--text-5)', fontSize: '13px', lineHeight: '1.8', textAlign: 'center' }}>{meta.what}</div>
            {meta.example && (
              <div style={{ padding: '12px 16px', background: 'var(--surface-2)', border: '1px solid var(--border-1)', borderLeft: `3px solid ${l1Color}`, borderRadius: 'var(--radius-md)', width: '100%', boxSizing: 'border-box' }}>
                <div style={{ color: 'var(--text-7)', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>example</div>
                <div style={{ color: 'var(--text-4)', fontSize: '13px', lineHeight: '1.7', fontStyle: 'italic' }}>{meta.example}</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button onClick={() => { setPbCreateType(activeL2); setShowPlaybookCreate(true); }} style={{
                background: 'none', border: '1px solid var(--border-1)',
                borderRadius: 'var(--radius-md)', color: 'var(--text-4)',
                fontSize: '12px', fontFamily: 'var(--font-serif)',
                padding: '8px 20px', cursor: 'pointer', letterSpacing: '0.04em',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-2)'; e.currentTarget.style.color = 'var(--text-1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-1)'; e.currentTarget.style.color = 'var(--text-4)'; }}
              >
                Build manually
              </button>
              <button onClick={() => setActiveL3('gameplan')} style={{
                background: 'var(--green-bg)', border: '1px solid var(--green)',
                borderRadius: 'var(--radius-md)', color: 'var(--green)',
                fontSize: '12px', fontFamily: 'var(--font-serif)',
                padding: '8px 20px', cursor: 'pointer', letterSpacing: '0.04em',
                transition: 'background var(--transition-fast)',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--green-border)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--green-bg)'}
              >
                Create with {aiName} →
              </button>
            </div>
          </div>
        );
      }

      const filtered = myItems.filter(i => !search || i.title.toLowerCase().includes(search.toLowerCase()));
      return (
        <div className={isGrid ? 'cards-grid' : undefined} style={{ display: isGrid ? 'grid' : 'block' }}>
          {filtered.map(item => {
            const preview = getItemPreview(item, isGrid ? 120 : 100);
            const isSelected = selected.has(item.id);
            const props = { item, accentColor: l1Color, typeLabel: TYPE_LABELS[item.item_type] || item.item_type, preview, actionLabel: 'Gameplan →', onAction: startGameplan, onDelete: deletePlaybookItem, selectMode, isSelected, onSelect: toggleSelect, fromCommunity: item._fromCommunity };
            return isGrid ? <ShiftCard key={item.id} {...props} /> : <ItemRowList key={item.id} {...props} />;
          })}
        </div>
      );
    }

    // ── L3: GAMEPLAN ─────────────────────────────────
    if (activeL3 === 'gameplan') {
      const myItems = playbookItems.filter(i => i.item_type === activeL2 && !i._fromCommunity);
      if (myItems.length === 0) return (
        <div style={{ padding: '48px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div style={{ color: 'var(--text-5)', fontSize: '14px', fontFamily: 'var(--font-serif)', textAlign: 'center' }}>No {TYPE_LABELS[activeL2] || activeL2}s yet.</div>
          <div style={{ color: 'var(--text-7)', fontSize: '12px', textAlign: 'center', maxWidth: '280px', lineHeight: '1.7' }}>Create one in My {TYPE_LABELS[activeL2] || activeL2}s first, then come back to gameplan with it.</div>
          <button onClick={() => setActiveL3('mine')} style={{
            background: 'none', border: '1px solid var(--border-1)',
            borderRadius: 'var(--radius-md)', color: 'var(--text-4)',
            fontSize: '12px', fontFamily: 'var(--font-serif)',
            padding: '8px 20px', cursor: 'pointer', letterSpacing: '0.04em', marginTop: '4px',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-2)'; e.currentTarget.style.color = 'var(--text-1)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-1)'; e.currentTarget.style.color = 'var(--text-4)'; }}
          >
            ← Go create one
          </button>
        </div>
      );
      return (
        <div className="cards-grid">
          {myItems.map(item => {
            const lastSummary = item.last_gameplan_summary || null;
            const preview = lastSummary || getItemPreview(item, 120);
            const hasSession = !!item.last_gameplan_summary;
            return (
              <div key={item.id} style={{
                padding: '24px', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-3)', borderLeft: `3px solid ${l1Color}`,
                display: 'flex', flexDirection: 'column', minHeight: '180px',
                cursor: 'default', position: 'relative', transition: 'background var(--transition-fast)',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-6)'; const d = e.currentTarget.querySelector('.gp-del'); if (d) d.style.opacity = '1'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-2)'; const d = e.currentTarget.querySelector('.gp-del'); if (d) d.style.opacity = '0'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <span style={{ background: `${l1Color}18`, border: `1px solid ${l1Color}55`, borderRadius: 'var(--radius-sm)', color: l1Color, fontSize: '12px', letterSpacing: '0.07em', padding: '2px 8px', fontFamily: 'var(--font-serif)' }}>
                    {TYPE_LABELS[item.item_type] || item.item_type}
                  </span>
                  <div style={{ color: 'var(--text-1)', fontSize: '15px', fontFamily: 'var(--font-serif)', fontWeight: 'bold', lineHeight: '1.3' }}>{item.title}</div>
                </div>
                {preview && (
                  <div style={{ flex: 1 }}>
                    {hasSession && <div style={{ color: 'var(--text-7)', fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>last session</div>}
                    <div style={{ color: 'var(--text-4)', fontSize: '13px', lineHeight: '1.7' }}>{preview}</div>
                  </div>
                )}
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={e => { e.stopPropagation(); startGameplan(item); }} style={{
                    background: 'var(--green-bg)', border: '1px solid var(--green)',
                    borderRadius: 'var(--radius-sm)', color: 'var(--green)',
                    fontSize: '12px', fontFamily: 'var(--font-serif)',
                    padding: '6px 16px', cursor: 'pointer', letterSpacing: '0.04em',
                    transition: 'background var(--transition-fast)',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--green-border)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--green-bg)'}
                  >
                    {hasSession ? 'Continue →' : 'Gameplan →'}
                  </button>
                </div>
                <button className="gp-del" onClick={e => { e.stopPropagation(); deletePlaybookItem(item.id); }} style={{
                  position: 'absolute', top: '14px', right: '14px',
                  background: 'none', border: 'none', color: 'var(--danger)',
                  cursor: 'pointer', padding: '2px', opacity: 0, transition: 'opacity var(--transition-fast)',
                }}>
                  <TrashIcon />
                </button>
              </div>
            );
          })}
        </div>
      );
    }

    // ── L3: COMMUNITY ────────────────────────────────
    if (activeL3 === 'community') {
      const communityItems = playbookItems.filter(i => i.item_type === activeL2 && i._fromCommunity);
      if (communityItems.length === 0) return (
        <div style={{ padding: '48px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div style={{ color: 'var(--text-7)', fontSize: '14px', fontFamily: 'var(--font-serif)' }}>
            No community {TYPE_LABELS[activeL2] || activeL2}s yet.
          </div>
          <div style={{ color: 'var(--text-8)', fontSize: '12px', textAlign: 'center', maxWidth: '280px', lineHeight: '1.7' }}>
            Battle-tested items from the community will appear here once verified.
          </div>
        </div>
      );
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {communityItems.map(item => (
            <div key={item.id} style={{
              padding: '16px 20px', background: 'var(--green-surface)',
              borderRadius: 'var(--radius-md)', border: '1px solid var(--green-border)',
              borderLeft: `3px solid ${l1Color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
              transition: 'background var(--transition-fast)',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--green-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--green-surface)'}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--text-1)', fontSize: '15px', fontFamily: 'var(--font-serif)', marginBottom: '4px' }}>{item.title}</div>
                {item.content?.purpose && <div style={{ color: 'var(--green)', fontSize: '12px' }}>{item.content.purpose}</div>}
                {item.use_count > 0 && <div style={{ color: 'var(--text-7)', fontSize: '11px', marginTop: '4px' }}>used {item.use_count}×</div>}
              </div>
              <button onClick={() => startGameplan(item)} style={{
                background: 'var(--gold-bg)', border: '1px solid var(--gold-border)',
                borderRadius: 'var(--radius-md)', color: 'var(--gold)',
                fontSize: '12px', fontFamily: 'var(--font-serif)',
                padding: '8px 18px', cursor: 'pointer', flexShrink: 0,
                transition: 'background var(--transition-fast)',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--gold-bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--gold-bg)'}
              >
                Gameplan →
              </button>
            </div>
          ))}
        </div>
      );
    }

    return null;
  }

  // ── RENDER ────────────────────────────────────────────
  return (
    <>
      {/* Quick-create overlay — fixed sibling, above panel */}
      {showPlaybookCreate && (
        <PlaybookCreateOverlay
          pbCreateType={pbCreateType}   setPbCreateType={setPbCreateType}
          pbCreateTitle={pbCreateTitle} setPbCreateTitle={setPbCreateTitle}
          pbCreateContent={pbCreateContent} setPbCreateContent={setPbCreateContent}
          pbCreateTags={pbCreateTags}   setPbCreateTags={setPbCreateTags}
          createPlaybookItem={createPlaybookItem}
          onClose={() => {
            setShowPlaybookCreate(false);
            setPbCreateTitle('');
            setPbCreateContent('');
            setPbCreateTags('');
          }}
        />
      )}

      <div className="shift-panel-overlay">
        <ShiftPanelHeader
          title="Playbook"
          tabs={[
            { label: 'All',    color: '#888888' },
            { label: 'Lenses', color: '#4db8b8' },
            { label: 'Plays',  color: '#c8a96e' },
            { label: 'Reads',  color: '#7a9e82' },
            { label: 'Frames', color: '#9b8fc4' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          actionButton={{ label: '+ Create with Shift', onClick: handleCreate }}
          onClose={onClose}
        />
        <div style={{ flexShrink: 0 }}>{toolbar}</div>
        <div className="panel-content">
          <div className="panel-content-inner">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
}
